import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/marketplace/RichTextEditor';
import SEOFieldsBlock from '@/components/marketplace/SEOFieldsBlock';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Globe } from 'lucide-react';

const SECTIONS = [
  { key: 'sell', label: 'Sell (/sell)' },
  { key: 'rent', label: 'Rent (/rent)' },
  { key: 'wanted', label: 'Wanted (/wanted)' },
  { key: 'service', label: 'Services (/services)' },
];

// Admin editor for the fixed top-level section landing pages. URLs stay fixed;
// only content/SEO fields are edited here (mirrors the category content CMS).
const SectionPagesTab = () => {
  const [active, setActive] = useState('sell');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async (t) => {
    setForm(null);
    const { data } = await api.get(`/section-pages/${t}`);
    setForm({ faqs: [], ...data });
  };
  useEffect(() => { load(active); }, [active]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFaq = (i, k, v) => setForm(f => {
    const faqs = [...(f.faqs || [])];
    faqs[i] = { ...faqs[i], [k]: v };
    return { ...f, faqs };
  });
  const addFaq = () => setForm(f => ({ ...f, faqs: [...(f.faqs || []), { q: '', a: '' }] }));
  const removeFaq = (i) => setForm(f => ({ ...f, faqs: (f.faqs || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title || '', meta_title: form.meta_title || '',
        meta_description: form.meta_description || '',
        canonical_url: form.canonical_url || '',
        focus_keyword: form.focus_keyword || '',
        og_image_url: form.og_image_url || '',
        og_image_alt: form.og_image_alt || '',
        og_title: form.og_title || '',
        og_description: form.og_description || '',
        schema_type: form.schema_type || '',
        intro_html: form.intro_html || '', content_html: form.content_html || '',
        robots_meta: form.robots_meta || '',
        faqs: (form.faqs || []).filter(f => (f.q || '').trim() && (f.a || '').trim()),
      };
      await api.put(`/section-pages/${active}`, payload);
      toast.success('Section page saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-5' data-testid='section-pages-tab'>
      <div className='flex items-center gap-2'>
        <Globe className='h-4 w-4 text-primary' />
        <h2 className='text-lg font-semibold'>Section landing pages</h2>
      </div>
      <p className='text-sm text-muted-foreground'>Manage SEO + content for the fixed top-level pages. URLs stay clean and fixed; only content changes.</p>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList>
          {SECTIONS.map(s => <TabsTrigger key={s.key} value={s.key} data-testid={`section-tab-${s.key}`}>{s.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {!form ? <div className='py-8 text-center text-muted-foreground'>Loading…</div> : (
        <Card className='p-5 space-y-4'>
          <div className='grid gap-4 md:grid-cols-2'>
            <div>
              <Label>Display title (H1)</Label>
              <Input value={form.title || ''} onChange={e => set('title', e.target.value)} data-testid='section-title' />
            </div>
          </div>

          {/* Full SEO block — same component + scoring used by Blog & Categories */}
          <div className='pt-2 border-t'>
            <SEOFieldsBlock
              values={form}
              onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
              allowAdvanced
              contentType='page'
              compact
              testIdPrefix='section-seo'
            />
          </div>
          <div>
            <Label>Intro content</Label>
            <RichTextEditor value={form.intro_html || ''} onChange={(html) => set('intro_html', html)} />
          </div>
          <div>
            <Label>Main content</Label>
            <RichTextEditor value={form.content_html || ''} onChange={(html) => set('content_html', html)} />
          </div>

          <div>
            <div className='flex items-center justify-between mb-2'>
              <Label>FAQs (shown on page + emitted as FAQPage schema)</Label>
              <Button size='sm' variant='outline' onClick={addFaq} data-testid='section-add-faq'><Plus className='h-3.5 w-3.5 mr-1' /> Add FAQ</Button>
            </div>
            <div className='space-y-3'>
              {(form.faqs || []).map((f, i) => (
                <div key={i} className='rounded border p-3 space-y-2' data-testid={`section-faq-${i}`}>
                  <div className='flex gap-2'>
                    <Input placeholder='Question' value={f.q || ''} onChange={e => setFaq(i, 'q', e.target.value)} data-testid={`section-faq-q-${i}`} />
                    <Button size='icon' variant='ghost' onClick={() => removeFaq(i)} data-testid={`section-faq-del-${i}`}><Trash2 className='h-4 w-4 text-destructive' /></Button>
                  </div>
                  <Textarea rows={2} placeholder='Answer' value={f.a || ''} onChange={e => setFaq(i, 'a', e.target.value)} data-testid={`section-faq-a-${i}`} />
                </div>
              ))}
              {(form.faqs || []).length === 0 && <p className='text-xs text-muted-foreground'>No FAQs — FAQPage schema will not be emitted (correct: never fake FAQ schema).</p>}
            </div>
          </div>

          <div className='flex justify-end'>
            <Button onClick={save} disabled={saving} data-testid='section-save'><Save className='h-4 w-4 mr-1.5' /> {saving ? 'Saving…' : 'Save section'}</Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SectionPagesTab;
